import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { CoMark } from "@/components/co-mark";
import { MobileBottomNav } from "@/components/mobile-bottom-nav";
import { MobileNav } from "@/components/mobile-nav";
import { MobileNavProvider } from "@/components/mobile-nav-context";
import { ProfileMenu } from "@/components/profile-menu";
import { SideNav } from "@/components/side-nav";
import { ThemeToggle } from "@/components/theme-toggle";
import { cn } from "@/cn";
import { instrumentSerif } from "@/lib/fonts";
import { getOrgWorkspace } from "@/api/org";
import { loadNavCollapsed, persistNavCollapsed } from "@/lib/nav-items";
import { todayHref } from "@/lib/today-position";

export function AppShell({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const settingsMode = pathname.startsWith("/settings");
  const fullBleedMode = pathname === "/" || pathname === "/ai-assistant";
  const [collapsed, setCollapsed] = useState(() => loadNavCollapsed());

  const { data: orgData } = useQuery({
    queryKey: ["org"],
    queryFn: getOrgWorkspace,
    staleTime: 60_000,
    enabled: !settingsMode,
  });
  const orgName = orgData?.org?.name;

  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev;
      persistNavCollapsed(next);
      return next;
    });
  }

  if (settingsMode) {
    return <>{children}</>;
  }

  return (
    <MobileNavProvider>
      <MobileNav />
      <div className="flex min-h-screen md:h-screen md:overflow-hidden">
        <aside
          className={cn(
            "sticky top-0 hidden h-screen shrink-0 flex-col overflow-x-visible overflow-y-auto border-r border-border bg-surface/30 transition-[width] duration-200 ease-out md:flex",
            collapsed ? "w-16 p-2" : "w-60 p-4",
          )}
        >
          <Link
            to={todayHref()}
            className={cn(
              "mb-6 flex items-center px-1",
              collapsed ? "justify-center gap-0" : "mb-8 gap-2.5",
            )}
            title="CEO Command"
          >
            <CoMark size={collapsed ? 28 : 32} />
            {!collapsed && (
              <span
                className={`${instrumentSerif.className} relative -top-px text-2xl font-normal tracking-tight text-landing`}
              >
                CEO Command
              </span>
            )}
          </Link>
          {!collapsed && orgName && (
            <p
              className="mb-4 truncate px-1 text-xs text-muted-foreground"
              title={orgName}
            >
              {orgName}
            </p>
          )}

          <SideNav collapsed={collapsed} />

          <div className="mt-auto space-y-3 pt-4">
            <ProfileMenu collapsed={collapsed} />
            <button
              type="button"
              onClick={toggleCollapsed}
              aria-expanded={!collapsed}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              className={cn(
                "flex w-full items-center rounded-md text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground",
                collapsed ? "justify-center py-2" : "gap-2 px-3 py-2 text-sm",
              )}
            >
              {collapsed ? (
                <PanelLeftOpen className="size-4" />
              ) : (
                <>
                  <PanelLeftClose className="size-4" />
                  <span>Collapse</span>
                </>
              )}
            </button>
            <div
              className={cn(
                "flex items-center px-1",
                collapsed ? "justify-center" : "justify-between",
              )}
            >
              {!collapsed && (
                <span
                  className={`${instrumentSerif.className} text-sm text-faint`}
                >
                  ceo-cmd · v0
                </span>
              )}
              <ThemeToggle />
            </div>
          </div>
        </aside>

        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden pb-[calc(3.5rem+env(safe-area-inset-bottom))] md:h-screen md:pb-0 md:pl-5">
          <div
            className={cn(
              "min-h-0 flex-1",
              fullBleedMode
                ? "overflow-hidden p-3 md:px-4 md:pb-4 md:pt-6"
                : "mx-auto w-full max-w-6xl overflow-x-hidden overflow-y-auto px-4 py-6 sm:px-6 sm:py-8 md:px-8 md:py-10",
            )}
          >
            {children}
          </div>
        </main>
      </div>
      <MobileBottomNav />
    </MobileNavProvider>
  );
}
